import { CommonModule, DatePipe } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Component, Inject, ViewChild, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule, MatCalendar, MatCalendarCellClassFunction } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { provideNativeDateAdapter } from '@angular/material/core';
import { Bundle, Schedule, Slot } from 'fhir/r4';
import { EMPTY, catchError, expand, map, of, reduce } from 'rxjs';
import { backendEndPointToken } from '../app.config';
import { LoadingUIEnabled } from '../loading.interceptor';
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';

@Component({
  selector: 'app-schedule-details-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatDividerModule,
    MatIconModule,
    MatProgressSpinnerModule,
    ReferenceDisplayDirective
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './schedule-details-dialog.component.html',
  styleUrls: ['./schedule-details-dialog.component.scss']
})
export class ScheduleDetailsDialogComponent {
  @ViewChild(MatCalendar) private calendar?: MatCalendar<Date>;

  private readonly http = inject(HttpClient);
  private readonly backendUrl = inject(backendEndPointToken);

  slots: Slot[] = [];
  filteredSlots: Slot[] = [];
  loadingSlots = false;
  selectedDate: Date | null = null;
  calendarStartAt = new Date();
  slotDayKeys = new Set<string>();

  dateClass: MatCalendarCellClassFunction<Date> = (date: Date, view: 'month' | 'year' | 'multi-year'): string => {
    if (view !== 'month') return '';
    return this.slotDayKeys.has(this.toLocalDayKey(date)) ? 'available-date' : '';
  };

  constructor(@Inject(MAT_DIALOG_DATA) public readonly schedule: Schedule) {
    const start = this.toDate(schedule?.planningHorizon?.start);
    if (start) {
      this.calendarStartAt = start;
      this.selectedDate = start;
    }
    this.loadSlots();
  }

  get actorReference(): string {
    return this.schedule?.actor?.[0]?.reference ?? '';
  }

  get activeLabel(): string {
    return this.schedule?.active === false ? 'Inactive' : 'Active';
  }

  get totalSlots(): number {
    return this.slots.length;
  }

  get freeSlots(): number {
    return this.slots.filter((slot) => (slot.status ?? '').toLowerCase() === 'free').length;
  }

  get busySlots(): number {
    return this.totalSlots - this.freeSlots;
  }

  onDateSelected(date: Date | null): void {
    this.selectedDate = date;
    this.updateFilteredSlots();
  }

  formatSlotTime(slot: Slot): string {
    const start = this.toDate(slot.start);
    const end = this.toDate(slot.end);
    if (!start || !end) return 'Unknown time';
    return `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }

  private loadSlots(): void {
    const scheduleId = this.schedule?.id;
    if (!scheduleId) return;

    this.loadingSlots = true;
    const ctx = new HttpContext().set(LoadingUIEnabled, false);
    this.fetchAllBundleResources<Slot>(
      `${this.backendUrl}/Slot?schedule=Schedule/${encodeURIComponent(scheduleId)}&_count=200`,
      ctx
    ).pipe(
      map((resources) => resources.filter((resource): resource is Slot => resource?.resourceType === 'Slot')),
      catchError(() => of([] as Slot[]))
    ).subscribe((slots) => {
      this.slots = slots.sort((a, b) => String(a.start ?? '').localeCompare(String(b.start ?? '')));
      this.slotDayKeys = new Set(this.slots
        .map((slot) => this.toDate(slot.start))
        .filter((date): date is Date => !!date)
        .map((date) => this.toLocalDayKey(date)));

      if (!this.selectedDate && this.slots.length) {
        this.selectedDate = this.toDate(this.slots[0].start);
      }

      this.loadingSlots = false;
      this.updateFilteredSlots();
      queueMicrotask(() => this.calendar?.updateTodaysDate());
    });
  }

  private fetchAllBundleResources<T>(initialUrl: string, context: HttpContext, maxPages = 100) {
    return this.http.get<Bundle<T>>(initialUrl, { context }).pipe(
      expand((bundle, pageIndex) => {
        if (pageIndex + 1 >= maxPages) return EMPTY;

        const nextLink = (bundle?.link ?? []).find(
          (link) => String(link?.relation ?? '').toLowerCase() === 'next'
        )?.url;
        const nextUrl = this.normalizeFhirNextLink(nextLink);

        return nextUrl ? this.http.get<Bundle<T>>(nextUrl, { context }) : EMPTY;
      }),
      map((bundle) => (bundle?.entry ?? []).map((entry) => entry.resource as T).filter(Boolean)),
      reduce((resources, pageResources) => resources.concat(pageResources), [] as T[])
    );
  }

  private normalizeFhirNextLink(nextLink: string | undefined): string | null {
    const value = String(nextLink ?? '').trim();
    if (!value) return null;

    try {
      const nextUrl = new URL(value, `${this.backendUrl.replace(/\/$/, '')}/`);
      const slotPathIndex = nextUrl.pathname.lastIndexOf('/Slot');
      if (slotPathIndex < 0) return value;

      const backendBase = this.backendUrl.replace(/\/$/, '');
      return `${backendBase}${nextUrl.pathname.slice(slotPathIndex)}${nextUrl.search}${nextUrl.hash}`;
    } catch {
      return value;
    }
  }

  private updateFilteredSlots(): void {
    if (!this.selectedDate) {
      this.filteredSlots = [];
      return;
    }

    const selectedKey = this.toLocalDayKey(this.selectedDate);
    this.filteredSlots = this.slots.filter((slot) => {
      const date = this.toDate(slot.start);
      return date ? this.toLocalDayKey(date) === selectedKey : false;
    });
  }

  private toDate(value: any): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private toLocalDayKey(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
